import { Link, useNavigate } from "react-router-dom";
import {
  Drop, Plug, Lightning, Bug, Sink, FireExtinguisher, PaintBrush, Wrench, Wind,
  MapPin, Image, Chat, ChatCircle, Robot, WarningOctagon, Warning, ShieldCheck,
  Hand, Play, Check, X, Repeat, Sparkle, User, FilePdf, Lock, ClipboardText,
} from "@phosphor-icons/react";
import "./jobcard.css";
import InlineWoFix from "@/components/InlineWoFix";

// -----------------------------------------------------------------------------
// Trade → icon + color-tone mapping
// -----------------------------------------------------------------------------
const TRADE = {
  "Plumbing":            { Icon: Drop,     tone: "cyan"    },
  "Electrical":          { Icon: Lightning,tone: "amber"   },
  "HVAC":                { Icon: Wind,     tone: "sky"     },
  "Appliance":           { Icon: Plug,     tone: "violet"  },
  "Pest":                { Icon: Bug,      tone: "emerald" },
  "Drywall/Paint":       { Icon: PaintBrush,tone:"rose"    },
  "Roof":                { Icon: Wrench,   tone: "stone"   },
  "Windows/Doors":       { Icon: Wrench,   tone: "teal"    },
  "Landscaping":         { Icon: Wrench,   tone: "green"   },
  "High Water Usage":    { Icon: Drop,     tone: "sky"     },
  "General Maintenance": { Icon: Wrench,   tone: "slate"   },
};
const TRADE_DEFAULT = { Icon: Wrench, tone: "slate" };

const TONE_BG = {
  cyan: "bg-cyan-50 text-cyan-600",
  amber: "bg-amber-50 text-amber-600",
  sky: "bg-sky-50 text-sky-600",
  violet: "bg-violet-50 text-violet-600",
  emerald: "bg-emerald-50 text-emerald-600",
  rose: "bg-rose-50 text-rose-600",
  stone: "bg-stone-100 text-stone-600",
  teal: "bg-teal-50 text-teal-600",
  green: "bg-green-50 text-green-600",
  slate: "bg-slate-100 text-slate-500",
  red: "bg-red-50 text-red-600",
};

// -----------------------------------------------------------------------------
// Card ribbon color — derived heat cue
// -----------------------------------------------------------------------------
function ribbonClass(job) {
  if (job.habitability || job.life_safety) return "rib-red";
  if (job.status === "completed") return "rib-emerald";
  if (job.status === "in_progress") return "rib-cyan";
  if (job.status === "pool" || (!job.assigned_to && !job.assigned_to_name)) return "rib-slate";
  if (job.priority === "urgent" || job.priority === "high") return "rib-amber";
  return "rib-sky";
}

// -----------------------------------------------------------------------------
// Age in days
// -----------------------------------------------------------------------------
function ageDays(iso) {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d >= 0 ? d : null;
}

// -----------------------------------------------------------------------------
// Avatar helpers
// -----------------------------------------------------------------------------
const AVATAR_COLORS = ["bg-orange-500","bg-sky-500","bg-emerald-500","bg-purple-500","bg-fuchsia-500","bg-rose-500","bg-amber-500","bg-teal-500"];
function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name = "") {
  let h = 0; for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// =============================================================================
// JobCard
// =============================================================================
export default function JobCard({ job, selected, onToggle, onQuickClose, onWoFixed }) {
  const nav = useNavigate();
  const rib = ribbonClass(job);
  const trade = TRADE[job.category] || TRADE_DEFAULT;
  const { Icon: TradeIcon } = trade;
  const tone = job.habitability || job.life_safety ? "red" : trade.tone;
  const age = ageDays(job.created_at);
  const critical = job.habitability || job.life_safety;

  const goToJob = (e) => {
    if (e.target.closest("input,button,a")) return;
    nav(`/admin/jobs/${job.id}`);
  };

  // Primary CTA based on state
  let primary;
  if (critical && job.status !== "completed") {
    primary = { label: "Escalate", cls: "btn-red", to: `/admin/jobs/${job.id}` };
  } else if (job.status === "completed") {
    primary = { label: "PDF", cls: "btn-slate", to: `/admin/jobs/${job.id}` };
  } else if (job.status === "in_progress") {
    primary = { label: "Track", cls: "btn-cyan", to: `/admin/jobs/${job.id}` };
  } else if (job.status === "pool") {
    primary = { label: "Claim", cls: "btn-emerald", to: `/admin/jobs/${job.id}` };
  } else {
    primary = { label: "Open", cls: "btn-primary", to: `/admin/jobs/${job.id}` };
  }

  return (
    <div
      onClick={goToJob}
      data-testid={`job-card-${job.id}`}
      className={`jc-card ${critical ? "jc-critical" : ""} ${selected ? "jc-selected" : ""} px-3 py-3 cursor-pointer`}
    >
      <div className={`jc-rib ${rib}`}/>

      <div className="pl-2 flex items-start gap-3">
        {/* trade icon dot */}
        <div className={`jc-trade-dot ${TONE_BG[tone] || TONE_BG.slate}`} title={job.category || "General"}>
          <TradeIcon size={18} weight="fill"/>
        </div>

        <div className="min-w-0 flex-1">
          {/* row 1 — title + age chip */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[14px] font-black text-slate-900 leading-tight break-words" data-testid={`job-title-${job.id}`}>
                {job.recurrence?.active && <Repeat size={12} weight="bold" className="inline mr-1 text-cyan-600" title="Recurring"/>}
                {job.title || "(untitled)"}
              </div>
              <div className="text-[12px] text-slate-500 mt-0.5 truncate">
                <MapPin size={11} weight="fill" className="inline -mt-0.5 mr-0.5 text-slate-400"/>
                {job.property_address || "No address"}
                {job.unit && <span className="ml-1 font-semibold text-fuchsia-600">#{job.unit}</span>}
              </div>
            </div>
            {age != null && (
              <span className={`chip shrink-0 ${age > 25 && critical ? "bg-red-100 text-red-700" : age > 25 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                {age}d
              </span>
            )}
          </div>

          {/* row 2 — status/heat chips */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {critical && (
              <span className="chip bg-red-50 text-red-700">
                <WarningOctagon size={10} weight="fill"/>
                {job.habitability ? "Habitability" : "Life Safety"}
              </span>
            )}
            {job.priority && ["urgent","high"].includes(job.priority) && !critical && (
              <span className={`chip ${job.priority === "urgent" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
              </span>
            )}
            {job.status === "in_progress" && (
              <span className="chip bg-cyan-50 text-cyan-700"><Lightning size={10} weight="fill"/> Active</span>
            )}
            {job.status === "completed" && (
              <span className="chip bg-emerald-100 text-emerald-700"><Check size={10} weight="bold"/> Done</span>
            )}
            {job.status === "pool" && (
              <span className="chip bg-slate-100 text-slate-600">Pool</span>
            )}
            {job.needs_docs && !job.docs_completed_at && (
              <span className="chip bg-amber-50 text-amber-700"><ClipboardText size={10} weight="fill"/> Docs</span>
            )}
            {job.category && !critical && (
              <span className="chip bg-slate-50 text-slate-600">{job.category}</span>
            )}
            {job.partner_handled && (
              <span className="chip bg-violet-50 text-violet-700" data-testid="job-card-partner-badge">{job.partner_handled}</span>
            )}
            {job.permission_to_enter === "no" && (
              <span className="chip bg-rose-50 text-rose-700" data-testid="job-card-pte-badge">No entry w/o resident</span>
            )}
            {job.work_order_number && (
              <span className="ml-auto text-[10px] font-mono font-bold text-slate-400 shrink-0">WO#{job.work_order_number}</span>
            )}
            <InlineWoFix
              jobId={job.id}
              currentWo={job.work_order_number || ""}
              onSaved={() => onWoFixed && onWoFixed(job.id)}
              variant="row"
            />
          </div>

          {/* row 3 — tech + counts + primary CTA */}
          <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-100">
            <label className="jc-check" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggle && onToggle(job.id)}
                data-testid={`row-select-${job.id}`}
                aria-label={`Select ${job.title}`}
              />
            </label>
            {job.assigned_to_name ? (
              <>
                <div className={`avatar ${avatarColor(job.assigned_to_name)}`} title={job.assigned_to_name}>
                  {initials(job.assigned_to_name)}
                </div>
                <span className="text-[12px] font-semibold text-slate-700 truncate">{job.assigned_to_name}</span>
              </>
            ) : (
              <span className="text-[12px] text-slate-400 italic">Unassigned</span>
            )}
            <span className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500">
              {(job.photos_count || job.photos?.length) > 0 && (
                <span title="Photos"><Image size={11} weight="fill" className="inline -mt-0.5"/> {job.photos_count || job.photos?.length}</span>
              )}
              {(job.messages_count || job.messages?.length) > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span title="Messages"><ChatCircle size={11} weight="fill" className="inline -mt-0.5"/> {job.messages_count || job.messages?.length}</span>
                </>
              )}
            </span>
            <Link
              to={primary.to}
              onClick={(e) => e.stopPropagation()}
              data-testid={`job-card-cta-${job.id}`}
              className={`jc-btn ${primary.cls} ml-1`}
            >
              {primary.label}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
